import React from 'react';
import { type Editor, type Element } from 'slate';
import classnames from 'classnames';
import Shortcut from '../util/Shortcut';
import { type ToolbarSubmenu } from '../types/aha-editor';
import type AhaEditorV2Controller from '../controllers/AhaEditorV2Controller';
import toolbarStyles from '../components/Toolbar/Toolbar.module.css';
import { renderToolIcon } from '../components/Toolbar/toolbarUtils';
import type { Placement } from '@floating-ui/dom';

export interface ToolArgs {
  editor: Editor;
  element?: Element;
  toolbarShowModal?: (tool: typeof Tool) => void; // Show the modal verion of a tool instead of the toolbar.
  toolbarHideModal?: () => void; // Close the modal version of a tool.
  [key: string]: any; // Pass arbitrary tool-specific arguments.
}

export interface ToolArgsWithController extends ToolArgs {
  controller: AhaEditorV2Controller;
}

// Base class for all tools. A tool is snippet of code that performs an action
// and can be triggered by the button, menu or keyboard shortcut.
class Tool {
  // Name of the tool.
  static toolName = 'Tool name';

  // Shortcut for the tool.
  static shortcut = '';

  static style?: 'normal' | 'danger';

  static submenuTools: (() => (typeof Tool)[][]) | undefined;
  static submenuType: 'icons' | 'list-toggles' = 'icons';
  static menuItemClass = '';

  static get _shortcut() {
    if (this.shortcut.length === 0) return null;
    return new Shortcut(this.shortcut);
  }

  static get label(): string | JSX.Element {
    return this.toolName;
  }

  static _ariaLabel: string | undefined;
  static get ariaLabel(): string {
    return this._ariaLabel ?? this.toolName;
  }

  static set ariaLabel(value: string) {
    this._ariaLabel = value;
  }

  static size = 'regular';

  static get currentPlatformShortcut() {
    const shortcut = this._shortcut;
    if (!shortcut) return null;

    return shortcut.forCurrentPlatform;
  }

  // Font-awesome icon to display for the tool. Override `renderToolbarButtonIcon` to
  // render something other than an icon.
  static iconClass: string = 'fa-regular fa-square-question';

  // SVG icon to display for the tool. Will be rendered using
  // svgToIcon, and takes priority over iconClass.
  static iconSvg: any;

  // Element type to use for the icon. Defaults to 'i' for font-awesome icons,
  // but can be overridden to use other icon components like 'aha-icon'.
  static iconElement: string = 'i';

  // Submenu to show in the toolbar
  static toolbarSubmenu?: () => ToolbarSubmenu;

  // Renders the text in the tool's hover tooltip. Override to
  // generate the tooltip dynamically.
  static tooltip(toolArgs: ToolArgsWithController): string {
    return this.ariaLabel;
  }

  // Perform the action of the tool.
  static action(toolActionArgs: ToolArgsWithController): void {
    // Override in sub-class.
    throw new Error(
      'Tool action not implemented, override the "action" method in the subclass.'
    );
  }

  // Whether the tool is available or not. If it's not available, it should be hidden entirely.
  static available(toolArgs: ToolArgsWithController): boolean {
    return true;
  }

  // Whether the tool should appear enabled in the UI.
  static isEnabled(toolArgs: ToolArgsWithController): boolean {
    return true;
  }

  // Whether the tool is currently active - e.g. whether the tool has been
  // applied to the current selection.
  static isActive(toolArgs: ToolArgs): boolean {
    return false;
  }

  static renderToolbarButtonIcon(
    toolArgs: ToolArgsWithController
  ): JSX.Element {
    return renderToolIcon(this);
  }

  // Render the visual style of a toolbar button.
  static renderToolbarButton(toolArgs: ToolArgsWithController): JSX.Element {
    return (
      <div className={classnames(toolbarStyles.toolbarButton)}>
        {this.renderToolbarButtonIcon(toolArgs)}
      </div>
    );
  }

  static renderMenuIcon(toolArgs: ToolArgs): JSX.Element {
    return renderToolIcon(this, 'fa-fw');
  }

  // Implement `renderToolbarComponent` to render a custom toolbar button or trigger.
  static renderToolbarComponent(
    toolArgs: ToolArgsWithController
  ): JSX.Element | null {
    return null;
  }

  // Whether the modal should be opened when the toolbar is displayed. This
  // should be used sparingly. It is undefined if two tools try to open modals
  // on the same toolbar.
  static openToolbarModal(toolArgs: ToolArgs): boolean {
    return false;
  }

  // Render a modal view into the element toolbar for this tool.
  static renderToolbarModal(toolArgs: ToolArgs): JSX.Element {
    return <></>;
  }

  // The toolbar modal was closed by the user.
  static handleModalClosed(toolArgs: ToolArgs): void {}

  // The tool should not refocus the editor after the action. Usually because
  // the purpose of the action was to take focus somewhere else.
  static withoutRefocus = false;

  // Returns the placement for the tool's hover tooltip.Override to
  // customize tooltip placement for specific tools.
  static tooltipPlacement(toolArgs: ToolArgsWithController): Placement {
    return 'top';
  }
}

export default Tool;
